import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { TrailPool } from './TrailPool';
import { GamePalette } from './GamePalette';
import { Game } from './Game';

export class EnemyBossBullet extends GameObject {
  private static readonly SIZE = 16;
  private static readonly FILL = new Color(235, 87, 87);
  private static readonly GLOW = new Color(235, 87, 87, 40);
  private static readonly TRAIL_COLOR = new Color(235, 87, 87);
  private static readonly PARRY_FILL = new Color(78, 205, 196);
  private static readonly PARRY_GLOW = new Color(78, 205, 196, 60);
  private static readonly PARRY_DAMAGE = 90;

  private static readonly CORE_COLOR = new Color(230, 255, 250);

  private handler: Handler;
  private trailTick = 0;
  private readonly boundsRect = new Rectangle();
  private parried = false;

  constructor(x: number, y: number, id: ID, handler: Handler, vx: number, vy: number) {
    super(x, y, id);
    this.handler = handler;
    this.velX = vx;
    this.velY = vy;
  }

  isParried(): boolean {
    return this.parried;
  }

  parry(): void {
    this.parried = true;
    // Reverse and speed up
    this.velX *= -1.5;
    this.velY *= -1.5;
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = EnemyBossBullet.SIZE;
    this.boundsRect.height = EnemyBossBullet.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    this.x += this.velX;
    this.y += this.velY;
    // Remove if off-screen
    if (this.y >= Game.HEIGHT + 50 || this.y < -50 || this.x < -50 || this.x > Game.WIDTH + 50) {
      this.handler.removeObject(this);
      return;
    }

    const tc: Color = this.parried ? EnemyBossBullet.PARRY_FILL : EnemyBossBullet.TRAIL_COLOR;
    // Load-aware throttle (P1-A): identical under normal load, but once the shared trail pool
    // nears saturation (bullet-hell boss frames) we skip emission to cap the worst case.
    // Trails are purely cosmetic, so this never affects gameplay. trailTick still advances
    // every tick, so the emission cadence is unchanged when trails resume.
    if (++this.trailTick % 3 === 0 && TrailPool.liveCount() < 360) {
      TrailPool.add(
        this.x,
        this.y,
        tc,
        EnemyBossBullet.SIZE,
        EnemyBossBullet.SIZE,
        0.03,
        TrailPool.SHAPE_CIRCLE,
      );
    }

    // Parried bullets damage bosses on contact
    if (this.parried) {
      this.checkBossCollision();
    }
  }

  private checkBossCollision(): void {
    const objects: GameObject[] = this.handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      if (obj.getId() !== ID.EnemyBoss) continue;
      if (obj instanceof EnemyBossBullet) continue;
      if (this.getBounds().intersects(obj.getBounds())) {
        obj.takeDamage(EnemyBossBullet.PARRY_DAMAGE);
        Game.triggerScreenShake(6);
        // Spawn impact particles
        const accent = GamePalette.accent();
        for (let j = 0; j < 6; j++) {
          const angle = Math.random() * Math.PI * 2;
          TrailPool.add(
            this.x + Math.cos(angle) * 12,
            this.y + Math.sin(angle) * 12,
            accent,
            8,
            8,
            0.06,
            TrailPool.SHAPE_CIRCLE,
          );
        }
        this.handler.removeObject(this);
        return;
      }
    }
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const ix = Math.trunc(this.x);
    const iy = Math.trunc(this.y);

    if (this.parried) {
      g2.setColor(EnemyBossBullet.PARRY_GLOW);
      g2.fillOval(ix - 4, iy - 4, EnemyBossBullet.SIZE + 8, EnemyBossBullet.SIZE + 8);
      g2.setColor(EnemyBossBullet.PARRY_FILL);
      g2.fillOval(ix, iy, EnemyBossBullet.SIZE, EnemyBossBullet.SIZE);
      // Bright core
      g2.setColor(EnemyBossBullet.CORE_COLOR);
      g2.fillOval(ix + 3, iy + 3, EnemyBossBullet.SIZE - 6, EnemyBossBullet.SIZE - 6);
    } else {
      g2.setColor(EnemyBossBullet.GLOW);
      g2.fillOval(ix - 3, iy - 3, EnemyBossBullet.SIZE + 6, EnemyBossBullet.SIZE + 6);
      g2.setColor(EnemyBossBullet.FILL);
      g2.fillOval(ix, iy, EnemyBossBullet.SIZE, EnemyBossBullet.SIZE);
    }
  }
}
